import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../Helpers/Handler";

// Aadhaar through DigiLocker, the same way the customer app does it (customer_frontend
// Pages/Kyc/AadhaarStage.js): enter the number -> Generate -> the Surepass Digiboost widget
// opens right here on the page -> the customer completes OTP/consent in it -> the widget's
// onSuccess fetches and saves the Aadhaar data automatically. No second tab and no
// "I've completed it - verify now" step. The SDK is loaded by the <script> tag in
// public/index.html. If it ever fails to load, we fall back to opening the DigiLocker link
// in a new tab with a manual confirm, so the operator is never stuck.
//
// Used by the Customer and Associate Aadhaar stages; each passes its own link/verify URLs.
const AadhaarDigilocker = ({
    initialAadhaarNo = "",
    status,
    linkUrl,
    verifyUrl,
    canSubmit = true,
    noPermissionText = "You don't have permission to submit Aadhaar verification.",
    onVerified,
}) => {
    const [aadhaarNo, setAadhaarNo] = useState(initialAadhaarNo || "");
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState("form"); // form | widget | manual
    const [redo, setRedo] = useState(false);
    const clientIdRef = useRef(null);
    const widgetRef = useRef(null);
    const widgetId = useRef(`digilocker-widget-${Math.random().toString(36).slice(2, 9)}`).current;

    useEffect(() => { if (initialAadhaarNo) setAadhaarNo(initialAadhaarNo); }, [initialAadhaarNo]);
    useEffect(() => {
        const node = widgetRef.current;
        return () => { if (node) node.innerHTML = ""; };
    }, []);

    const reset = () => {
        if (widgetRef.current) widgetRef.current.innerHTML = "";
        clientIdRef.current = null;
        setMode("form");
        setLoading(false);
    };

    const verify = async () => {
        const clientId = clientIdRef.current;
        if (!clientId) return;
        setLoading(true);
        const response = await Handler({ method: "patch", url: verifyUrl, data: { clientId } });
        if (response?.success) {
            toast.success(response?.data?.message || "Aadhaar data fetched, pending verification");
            reset();
            setRedo(false);
            onVerified?.(response?.data);
        } else {
            toast.error(response?.data?.message || "Aadhaar verification failed");
            setLoading(false);
        }
    };

    const generate = async () => {
        if (!canSubmit) return toast.error(noPermissionText);
        if (aadhaarNo.length !== 12) return toast.error("Aadhaar number must be 12 digits");
        reset();
        setLoading(true);
        const response = await Handler({ method: "patch", url: linkUrl, data: { aadhaarNo } });
        const { token, clientId, url } = response?.data?.data || {};
        if (!response?.success || !token || !clientId) {
            toast.error(response?.data?.message || "Unable to generate the Aadhaar verification link");
            setLoading(false);
            return;
        }
        clientIdRef.current = clientId;

        if (typeof window.DigiboostSdk !== "function") {
            // Fallback only - the widget script didn't load (blocked network, ad blocker...).
            if (!url) {
                toast.error("DigiLocker is not available right now. Please try again.");
                reset();
                return;
            }
            setMode("manual");
            setLoading(false);
            window.open(url, "_blank", "noopener,noreferrer");
            return;
        }

        setMode("widget");
        // Next tick: the widget container below must be in the DOM before the SDK attaches to it.
        setTimeout(() => {
            try {
                window.DigiboostSdk({
                    gateway: "production",
                    token,
                    selector: `#${widgetId}`,
                    onSuccess: () => { verify(); },
                    onFailure: (error) => {
                        console.error("DigiLocker verification failed:", error);
                        toast.error("Aadhaar verification could not be completed. Please try again.");
                        reset();
                    },
                });
            } catch (error) {
                console.error("Could not start DigiLocker:", error);
                toast.error("Unable to start Aadhaar verification");
                reset();
            }
        }, 0);
    };

    const done = status === "verified" || status === "pending";
    if (done && !redo) {
        return (
            <div className={`alert ${status === "verified" ? "alert-success" : "alert-info"} small mb-0 d-flex justify-content-between align-items-center gap-2 flex-wrap`}>
                <span>{status === "verified" ? "Aadhaar is verified." : "Aadhaar is submitted and pending review."}</span>
                {status === "pending" && canSubmit ? (
                    <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setRedo(true)}>Fetch again</button>
                ) : null}
            </div>
        );
    }

    return (
        <>
            {!canSubmit ? <div className="alert alert-warning small mb-3">{noPermissionText}</div> : null}
            <div className="mb-3">
                <label htmlFor={`${widgetId}-no`} className="form-label">Aadhaar No</label>
                <input
                    type="text"
                    className="form-control"
                    id={`${widgetId}-no`}
                    autoComplete="off"
                    inputMode="numeric"
                    value={aadhaarNo}
                    onChange={(e) => { if (/^\d{0,12}$/.test(e.target.value)) setAadhaarNo(e.target.value); }}
                    placeholder="Enter 12-digit Aadhaar No"
                    maxLength={12}
                    disabled={loading || mode !== "form" || !canSubmit}
                />
            </div>

            <div className="mb-1 text-center">
                {mode === "form" ? (
                    <button type="button" className="btn btn-primary" onClick={generate} disabled={loading || !canSubmit || aadhaarNo.length !== 12}>
                        {loading ? "Generating..." : "Generate Link"}
                    </button>
                ) : null}

                {/* The Digiboost widget renders into this node. */}
                <div id={widgetId} ref={widgetRef} className={mode === "widget" ? "mb-2" : "d-none"} />

                {mode === "widget" ? (
                    <>
                        <div className="text-muted small mb-2">
                            {loading ? "Saving the Aadhaar details..." : "Ask the customer for the OTP sent to their Aadhaar-linked mobile and complete DigiLocker above."}
                        </div>
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={reset} disabled={loading}>Start Over</button>
                    </>
                ) : null}

                {mode === "manual" ? (
                    <>
                        <div className="alert alert-info small mb-2 text-start">
                            DigiLocker opened in a new tab (the on-page widget could not load). Complete it there, then come back and verify.
                        </div>
                        <button type="button" className="btn btn-outline-secondary btn-sm me-2" onClick={reset} disabled={loading}>Start Over</button>
                        <button type="button" className="btn btn-primary btn-sm" onClick={verify} disabled={loading}>{loading ? "Verifying..." : "Verify Now"}</button>
                    </>
                ) : null}
            </div>
        </>
    );
};

export default AadhaarDigilocker;
